import * as React from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Box,
    Checkbox,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { MouseEvent, useState } from 'react';
import { DateField, ReferenceField, useNotify, useRefresh } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import {
    deleteTask,
    updateTask,
    updateTaskDone,
    type Task,
    type TaskType,
} from '@wwf/sdk';

const postponeDueDateIso = (daysFromNow: number) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString();
};

export const TaskListItem = ({
    task,
    showMember,
    openEditDrawer,
}: {
    task: Task;
    showMember?: boolean;
    openEditDrawer?: (id: string | number) => void;
}) => {
    const navigate = useNavigate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);
    const handleClick = React.useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        },
        []
    );
    const handleCloseMenu = React.useCallback(() => {
        setAnchorEl(null);
    }, []);

    const { mutate: toggleDone, isPending: isUpdatePending } = useMutation({
        mutationFn: () => updateTaskDone(task.id, { done: !task.doneDate }),
        onSuccess: () => refresh(),
        onError: error => notify(String(error), { type: 'error' }),
    });

    const { mutate: postpone } = useMutation({
        mutationFn: (dueDate: string) =>
            updateTask(task.id, {
                type: task.type as TaskType,
                text: task.text,
                dueDate,
                assignedMemberId: task.assignedMemberId,
            }),
        onSuccess: () => refresh(),
        onError: error => notify(String(error), { type: 'error' }),
    });

    const handleDelete = React.useCallback(async () => {
        handleCloseMenu();
        if (!window.confirm('Are you sure you want to delete this task?')) {
            return;
        }
        try {
            await deleteTask(task.id);
            refresh();
        } catch (error) {
            notify(String(error), { type: 'error' });
        }
    }, [handleCloseMenu, notify, refresh, task.id]);

    const handleEdit = React.useCallback(() => {
        setAnchorEl(null);
        if (openEditDrawer) {
            openEditDrawer(task.id);
        } else {
            navigate('/task', {
                state: { openEditDrawer: true, taskId: task.id },
            });
        }
    }, [openEditDrawer, task.id, navigate]);

    const handleCheck = React.useCallback(() => {
        toggleDone();
    }, [toggleDone]);
    const labelId = `checkbox-list-label-${task.id}`;

    return (
        <>
            <ListItem
                disablePadding
                secondaryAction={
                    <>
                        <IconButton
                            edge="end"
                            aria-label="task actions"
                            aria-controls={
                                open ? `task-${task.id}-menu` : undefined
                            }
                            onClick={handleClick}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            size="small"
                        >
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            id={`task-${task.id}-menu`}
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleCloseMenu}
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}
                        >
                            <MenuItem
                                onClick={() => {
                                    postpone(postponeDueDateIso(1));
                                    handleCloseMenu();
                                }}
                            >
                                Postpone to tomorrow
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    postpone(postponeDueDateIso(7));
                                    handleCloseMenu();
                                }}
                            >
                                Postpone to next week
                            </MenuItem>
                            <MenuItem onClick={handleEdit}>Edit</MenuItem>
                            <MenuItem onClick={handleDelete}>Delete</MenuItem>
                        </Menu>
                    </>
                }
                disableGutters
            >
                <Stack direction="row" flex={1}>
                    {/* Text area in the center - clickable to edit */}
                    <ListItemButton
                        onClick={handleEdit}
                        sx={{ flex: 1, justifyItems: 'flex-start' }}
                    >
                        <ListItemText
                            sx={{
                                textDecoration: !!task.doneDate
                                    ? 'line-through'
                                    : 'none',
                                flex: 2,
                                flexShrink: 0,
                            }}
                            id={labelId}
                        >
                            {task.type && task.type !== 'None' && (
                                <>
                                    <Typography
                                        component="strong"
                                        variant="body2"
                                    >
                                        {task.type}
                                    </Typography>
                                    &nbsp;-&nbsp;
                                </>
                            )}
                            {task.text}
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                component="div"
                            >
                                due <DateField source="dueDate" record={task} />
                            </Typography>
                        </ListItemText>
                        {showMember && (
                            <Box
                                component="div"
                                sx={{
                                    flex: 5,
                                    flexShrink: 0,
                                }}
                            >
                                <ReferenceField
                                    source="assignedMemberId"
                                    reference="member"
                                    record={task}
                                    link={false}
                                >
                                    <Box
                                        component="span"
                                        onClick={e => e.stopPropagation()}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        &nbsp;Assigned to:&nbsp;
                                        <MemberLightCard />
                                    </Box>
                                </ReferenceField>
                            </Box>
                        )}
                    </ListItemButton>
                    {/* Checkbox area on the left */}
                    <ListItemButton
                        onClick={handleCheck}
                        disabled={isUpdatePending}
                        alignItems="center"
                        sx={{
                            flex: 0,
                            justifyContent: 'center',
                            minWidth: '2.5em',
                            marginRight: 2,
                            paddingLeft: 4,
                        }}
                    >
                        <ListItemIcon>
                            <Checkbox
                                checked={!!task.doneDate}
                                tabIndex={-1}
                                disableRipple
                            />
                        </ListItemIcon>
                    </ListItemButton>
                </Stack>
            </ListItem>
        </>
    );
};
